/**
 * Serializable state and UTC-only date maths for Plot period comparisons.
 *
 * Period A deliberately keeps using the app's existing `startDate`/`endDate`
 * atoms.  The comparison atom owns only A's label and the additional periods,
 * so Export and every older workflow still have one authoritative date range.
 */

import { activePreset, Atom, endDate, selectedShip, startDate, type DatePreset } from './store.ts';

export const PERIOD_COMPARISON_VERSION = 1 as const;
export const REFERENCE_PERIOD_ID = 'period-a';
export const MAX_COMPARISON_PERIODS = 4;
export const MAX_PERIOD_LABEL = 40;

export type ComparisonTimeAxis = 'elapsed' | 'utc';

export interface ComparisonPeriod {
  id: string;
  label: string;
  start: string;
  end: string;
}

export interface PeriodComparison {
  version: typeof PERIOD_COMPARISON_VERSION;
  referenceLabel: string;
  additional: ComparisonPeriod[];
  timeAxis: ComparisonTimeAxis;
  linkNavigation: boolean;
}

export interface PlotPanelSpec extends ComparisonPeriod {
  ship: string;
}

export interface PeriodValidationIssue {
  periodId: string;
  field: 'start' | 'end' | 'label' | 'comparison';
  message: string;
}

interface PeriodDraft {
  ship: string;
  referenceStart: string;
  referenceEnd: string;
  comparison: PeriodComparison;
}

/** Active, serializable comparison. Null means the ordinary Plot flow. */
export const periodComparison = new Atom<PeriodComparison | null>(null);
/** Which editor is expanded. View-only and never stored in a URL. */
export const expandedPeriodId = new Atom<string | null>(null);
/** A visible explanation for a rejected/suspended comparison link or mode. */
export const periodComparisonNotice = new Atom<string | null>(null);

let draft: PeriodDraft | null = null;
let entryContext: { start: string; end: string; preset: DatePreset } | null = null;

function hasZone(value: string): boolean {
  return /(?:Z|[+-]\d\d:\d\d)$/i.test(value);
}

/** Parse a datetime as UTC, including the timezone-less values from datetime-local. */
export function utcEpochSeconds(value: string): number | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  const ms = Date.parse(hasZone(text) ? text : `${text}Z`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/** Normalized second-precision value used by inputs, requests and persistence. */
export function normalizeUtcDatetime(value: string): string | null {
  const sec = utcEpochSeconds(value);
  return sec == null ? null : new Date(sec * 1000).toISOString().slice(0, 19);
}

export function periodDurationSeconds(period: Pick<ComparisonPeriod, 'start' | 'end'>): number | null {
  const start = utcEpochSeconds(period.start);
  const end = utcEpochSeconds(period.end);
  return start == null || end == null || end <= start ? null : end - start;
}

export function formatPeriodDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return 'Invalid duration';
  if (seconds % 86400 === 0) {
    const n = seconds / 86400;
    return `${n} day${n === 1 ? '' : 's'}`;
  }
  if (seconds % 3600 === 0) {
    const n = seconds / 3600;
    return `${n} hour${n === 1 ? '' : 's'}`;
  }
  if (seconds % 60 === 0) {
    const n = seconds / 60;
    return `${n} minute${n === 1 ? '' : 's'}`;
  }
  return `${seconds} seconds`;
}

/** The equal-duration interval immediately before `period`. */
export function previousPeriod(
  period: Pick<ComparisonPeriod, 'start' | 'end'>,
  id = 'period-b',
  label = 'Previous period',
): ComparisonPeriod | null {
  const end = utcEpochSeconds(period.start);
  const duration = periodDurationSeconds(period);
  if (end == null || duration == null) return null;
  return {
    id,
    label,
    start: new Date((end - duration) * 1000).toISOString().slice(0, 19),
    end: new Date(end * 1000).toISOString().slice(0, 19),
  };
}

/** Resolve A from the global date context and append the stored periods. */
export function resolveComparisonPeriods(
  comparison: PeriodComparison | null = periodComparison.value,
  ship = selectedShip.value,
  referenceStart = startDate.value,
  referenceEnd = endDate.value,
): PlotPanelSpec[] {
  if (!comparison || !ship) return [];
  const aStart = normalizeUtcDatetime(referenceStart) ?? referenceStart;
  const aEnd = normalizeUtcDatetime(referenceEnd) ?? referenceEnd;
  return [
    {
      id: REFERENCE_PERIOD_ID,
      ship,
      label: comparison.referenceLabel,
      start: aStart,
      end: aEnd,
    },
    ...comparison.additional.map((period) => ({ ...period, ship })),
  ];
}

export function validatePeriods(periods: ComparisonPeriod[]): PeriodValidationIssue[] {
  const issues: PeriodValidationIssue[] = [];
  const ids = new Set<string>();
  for (const period of periods) {
    if (!period.id || ids.has(period.id)) {
      issues.push({ periodId: period.id || 'unknown', field: 'comparison', message: 'Period IDs must be unique.' });
    }
    ids.add(period.id);
    if (period.label.length > MAX_PERIOD_LABEL) {
      issues.push({ periodId: period.id, field: 'label', message: `Use at most ${MAX_PERIOD_LABEL} characters.` });
    }
    const start = utcEpochSeconds(period.start);
    const end = utcEpochSeconds(period.end);
    if (start == null) issues.push({ periodId: period.id, field: 'start', message: 'Enter a valid UTC start.' });
    if (end == null) issues.push({ periodId: period.id, field: 'end', message: 'Enter a valid UTC end.' });
    if (start != null && end != null && end <= start) {
      issues.push({ periodId: period.id, field: 'end', message: 'End must be after start.' });
    }
  }
  return issues;
}

export function comparisonIssues(
  comparison: PeriodComparison | null = periodComparison.value,
): PeriodValidationIssue[] {
  if (!comparison) return [];
  // Comparison opens on A alone and B is added by hand, so "nothing to compare
  // yet" is a normal state with its own instruction, not a validation error.
  if (comparison.additional.length === 0) {
    return [{ periodId: REFERENCE_PERIOD_ID, field: 'comparison', message: 'Add period B to compare.' }];
  }
  if (comparison.additional.length > MAX_COMPARISON_PERIODS - 1) {
    return [{
      periodId: REFERENCE_PERIOD_ID,
      field: 'comparison',
      message: `Compare between 2 and ${MAX_COMPARISON_PERIODS} periods.`,
    }];
  }
  return validatePeriods(resolveComparisonPeriods(comparison));
}

export function comparisonHasDifferentDurations(
  periods: ComparisonPeriod[] = resolveComparisonPeriods(),
): boolean {
  const durations = periods.map(periodDurationSeconds);
  return durations.length > 1 && durations.some((duration) => duration !== durations[0]);
}

export function comparisonHasOverlap(periods: ComparisonPeriod[] = resolveComparisonPeriods()): boolean {
  for (let i = 0; i < periods.length; i++) {
    const a0 = utcEpochSeconds(periods[i]!.start);
    const a1 = utcEpochSeconds(periods[i]!.end);
    if (a0 == null || a1 == null) continue;
    for (let j = i + 1; j < periods.length; j++) {
      const b0 = utcEpochSeconds(periods[j]!.start);
      const b1 = utcEpochSeconds(periods[j]!.end);
      if (b0 != null && b1 != null && a0 < b1 && b0 < a1) return true;
    }
  }
  return false;
}

export function elapsedX(timestampSeconds: number, periodStart: string): number {
  const origin = utcEpochSeconds(periodStart);
  return origin == null ? timestampSeconds : timestampSeconds - origin;
}

/** Map an elapsed navigation window back onto a period's immutable UTC bounds. */
export function mapElapsedWindow(
  period: Pick<ComparisonPeriod, 'start' | 'end'>,
  minElapsed: number,
  maxElapsed: number,
): { start: string; end: string } | null {
  const baseStart = utcEpochSeconds(period.start);
  const baseEnd = utcEpochSeconds(period.end);
  if (baseStart == null || baseEnd == null || !(maxElapsed > minElapsed)) return null;
  const start = Math.max(baseStart, baseStart + minElapsed);
  const end = Math.min(baseEnd, baseStart + maxElapsed);
  if (!(end > start)) return null;
  return {
    start: new Date(start * 1000).toISOString().slice(0, 19),
    end: new Date(end * 1000).toISOString().slice(0, 19),
  };
}

function cloneComparison(value: PeriodComparison): PeriodComparison {
  return { ...value, additional: value.additional.map((period) => ({ ...period })) };
}

/**
 * Whether there is anything to compare yet. Comparison mode opens on A alone;
 * until B exists nothing is plotted as a comparison, and nothing is put in a
 * link - a `pc=` with no periods would be refused on the way back in.
 */
export function comparisonReady(comparison: PeriodComparison | null = periodComparison.value): boolean {
  return !!comparison && comparison.additional.length > 0;
}

/** Display letter for a period by position: A, B, C, D. */
export function periodLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

/**
 * Enable comparison on period A alone, restoring a compatible session draft
 * when possible. B is added by hand: an automatic "previous period" was one
 * guess at what people wanted to compare against, and a wrong guess had to be
 * edited before anything useful happened.
 */
export function startPeriodComparison(): boolean {
  const ship = selectedShip.value;
  const reference = {
    id: REFERENCE_PERIOD_ID,
    label: 'Reference',
    start: normalizeUtcDatetime(startDate.value) ?? startDate.value,
    end: normalizeUtcDatetime(endDate.value) ?? endDate.value,
  };
  if (!ship || validatePeriods([reference]).length) return false;

  entryContext = {
    start: reference.start,
    end: reference.end,
    preset: activePreset.value,
  };
  startDate.set(reference.start);
  endDate.set(reference.end);
  if (draft?.ship === ship) {
    const restored = cloneComparison(draft.comparison);
    periodComparison.set(restored);
    expandedPeriodId.set(restored.additional[0]?.id ?? REFERENCE_PERIOD_ID);
  } else {
    periodComparison.set({
      version: PERIOD_COMPARISON_VERSION,
      referenceLabel: 'Reference',
      additional: [],
      timeAxis: 'elapsed',
      linkNavigation: true,
    });
    expandedPeriodId.set(null);
  }
  periodComparisonNotice.set(null);
  return true;
}

/** Stop/suspend comparison while retaining B-D for this session. */
export function stopPeriodComparison(notice: string | null = null): void {
  const comparison = periodComparison.value;
  const ship = selectedShip.value;
  if (comparison && ship) {
    draft = {
      ship,
      referenceStart: startDate.value,
      referenceEnd: endDate.value,
      comparison: cloneComparison(comparison),
    };
  }
  if (!notice) {
    const sameEntry =
      entryContext != null &&
      normalizeUtcDatetime(startDate.value) === normalizeUtcDatetime(entryContext.start) &&
      normalizeUtcDatetime(endDate.value) === normalizeUtcDatetime(entryContext.end);
    activePreset.set(sameEntry ? entryContext!.preset : 'custom');
    entryContext = null;
  }
  periodComparison.set(null);
  expandedPeriodId.set(null);
  periodComparisonNotice.set(notice);
}

/** Resume only an automatically suspended comparison, never one the user stopped. */
export function resumeSuspendedPeriodComparison(): boolean {
  if (!draft || !periodComparisonNotice.value || !selectedShip.value) return false;
  if (draft.ship !== selectedShip.value) return false;
  startDate.set(draft.referenceStart);
  endDate.set(draft.referenceEnd);
  periodComparison.set(cloneComparison(draft.comparison));
  expandedPeriodId.set(null);
  periodComparisonNotice.set(null);
  return true;
}

/** Forget active and suspended comparison state (regular saved views / Reset). */
export function clearPeriodComparison(): void {
  periodComparison.set(null);
  expandedPeriodId.set(null);
  periodComparisonNotice.set(null);
  draft = null;
  entryContext = null;
}

export function setReferenceLabel(label: string): void {
  const value = periodComparison.value;
  if (!value) return;
  periodComparison.set({ ...value, referenceLabel: label.slice(0, MAX_PERIOD_LABEL) });
}

export function updateComparisonPeriod(id: string, patch: Partial<Omit<ComparisonPeriod, 'id'>>): void {
  const value = periodComparison.value;
  if (!value) return;
  periodComparison.set({
    ...value,
    additional: value.additional.map((period) =>
      period.id === id
        ? {
            ...period,
            ...patch,
            label: patch.label !== undefined ? patch.label.slice(0, MAX_PERIOD_LABEL) : period.label,
          }
        : period,
    ),
  });
}

/**
 * The period a new or re-anchored period follows: the one listed just above
 * it. B follows A, C follows B - so "before" always means the row above,
 * which is what the editor says on the button.
 */
export function periodAbove(
  index: number,
  comparison: PeriodComparison | null = periodComparison.value,
): ComparisonPeriod | null {
  return resolveComparisonPeriods(comparison)[index - 1] ?? null;
}

/**
 * An id no period uses yet. Ids are stable handles, not positions: deriving
 * one from the count gave a second `period-c` after B was removed.
 */
function freshPeriodId(value: PeriodComparison): string {
  const used = new Set([REFERENCE_PERIOD_ID, ...value.additional.map((period) => period.id)]);
  for (let code = 98; ; code++) {
    const id = `period-${code <= 122 ? String.fromCharCode(code) : code}`;
    if (!used.has(id)) return id;
  }
}

/** Add the next period: the same length, immediately before the last one. */
export function addComparisonPeriod(): ComparisonPeriod | null {
  const value = periodComparison.value;
  if (!value || value.additional.length >= MAX_COMPARISON_PERIODS - 1) return null;
  const last = periodAbove(value.additional.length + 1, value);
  if (!last) return null;
  const next = previousPeriod(last, freshPeriodId(value), '');
  if (!next) return null;
  // Whatever is open stays open: adding a period is not a reason to move the
  // editor out from under the person using it.
  periodComparison.set({ ...value, additional: [...value.additional, next] });
  return next;
}

/** Remove a period. Removing the last one leaves A alone, still in comparison. */
export function removeComparisonPeriod(id: string): void {
  const value = periodComparison.value;
  if (!value) return;
  const additional = value.additional.filter((period) => period.id !== id);
  periodComparison.set({ ...value, additional });
  if (expandedPeriodId.value === id) expandedPeriodId.set(null);
}

export function setComparisonTimeAxis(timeAxis: ComparisonTimeAxis): void {
  const value = periodComparison.value;
  if (value && value.timeAxis !== timeAxis) periodComparison.set({ ...value, timeAxis });
}

export function setComparisonLinkNavigation(linkNavigation: boolean): void {
  const value = periodComparison.value;
  if (value && value.linkNavigation !== linkNavigation) {
    periodComparison.set({ ...value, linkNavigation });
  }
}

/** Stable JSON for `pc=` and Catalog context. Runtime/editor state is absent. */
export function serializePeriodComparison(value: PeriodComparison): string {
  return JSON.stringify({
    version: PERIOD_COMPARISON_VERSION,
    referenceLabel: value.referenceLabel,
    additional: value.additional.map((period) => ({
      id: period.id,
      label: period.label,
      start: normalizeUtcDatetime(period.start) ?? period.start,
      end: normalizeUtcDatetime(period.end) ?? period.end,
    })),
    timeAxis: value.timeAxis,
    linkNavigation: value.linkNavigation,
  });
}

export function parsePeriodComparison(raw: string): { value: PeriodComparison | null; error: string | null } {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || parsed.version !== PERIOD_COMPARISON_VERSION) {
      return { value: null, error: 'This period comparison uses an unsupported version.' };
    }
    if (typeof parsed.referenceLabel !== 'string' || parsed.referenceLabel.length > MAX_PERIOD_LABEL) {
      return { value: null, error: 'The reference period label is invalid.' };
    }
    if (!Array.isArray(parsed.additional) || parsed.additional.length < 1 || parsed.additional.length > 3) {
      return { value: null, error: 'A period comparison needs between 2 and 4 periods.' };
    }
    if (parsed.timeAxis !== 'elapsed' && parsed.timeAxis !== 'utc') {
      return { value: null, error: 'The comparison time-axis setting is invalid.' };
    }
    if (typeof parsed.linkNavigation !== 'boolean') {
      return { value: null, error: 'The comparison navigation setting is invalid.' };
    }
    const additional: ComparisonPeriod[] = [];
    for (const item of parsed.additional) {
      if (!item || typeof item !== 'object') return { value: null, error: 'A comparison period is invalid.' };
      const p = item as Record<string, unknown>;
      if (
        typeof p.id !== 'string' ||
        typeof p.label !== 'string' ||
        typeof p.start !== 'string' ||
        typeof p.end !== 'string'
      ) {
        return { value: null, error: 'A comparison period is missing required fields.' };
      }
      additional.push({
        id: p.id,
        label: p.label,
        start: normalizeUtcDatetime(p.start) ?? p.start,
        end: normalizeUtcDatetime(p.end) ?? p.end,
      });
    }
    const value: PeriodComparison = {
      version: PERIOD_COMPARISON_VERSION,
      referenceLabel: parsed.referenceLabel,
      additional,
      timeAxis: parsed.timeAxis,
      linkNavigation: parsed.linkNavigation,
    };
    const issues = [
      ...(additional.some((period) => period.id === REFERENCE_PERIOD_ID)
        ? [{ periodId: REFERENCE_PERIOD_ID, field: 'comparison' as const, message: 'Period IDs must be unique.' }]
        : []),
      ...validatePeriods(additional),
    ];
    return issues.length
      ? { value: null, error: issues[0]!.message }
      : { value, error: null };
  } catch {
    return { value: null, error: 'The period comparison link is malformed.' };
  }
}

/** Used by builder snapshots without exposing the module's private draft. */
export function cloneActivePeriodComparison(): PeriodComparison | null {
  return periodComparison.value ? cloneComparison(periodComparison.value) : null;
}

/** Restore a builder snapshot exactly; null intentionally clears the active mode. */
export function restoreActivePeriodComparison(value: PeriodComparison | null): void {
  periodComparison.set(value ? cloneComparison(value) : null);
}
